import type { CSSProperties } from 'react';
import { clsx } from 'clsx';
import { HomepageHeader, type HeaderTone } from '../sections/HomepageHeader';
import { marketingFontVariables } from './fonts';
import styles from './marketing.module.css';

interface SiteHeaderProps {
  /**
   * The page colour the bar's solid band paints, for a page whose ground is not
   * the design system's soft white. A hex literal here is deliberate: it is the
   * reviewed ground of a page body that has not been redesigned yet, so there is
   * no token for it to read. Keep it in sync with the page's own `<main>` class:
   * the two are written in different cases (`band="#faf7f3"` against
   * `bg-[#FAF7F3]`), so a grep for one will not find the other.
   */
  readonly band?: `#${string}`;
  /**
   * The tone rendered before hydration. Defaults to `page` – the solid band –
   * because that is what an ordinary page ground wants and a wrong first paint
   * shows as a flash. A page whose top band is dark (the Reputation
   * photograph) passes `dark` so the glass bar is already correct in
   * the SSR HTML.
   */
  readonly initialTone?: HeaderTone;
}

/**
 * The site-wide top bar (HAL-1615).
 *
 * `HomepageHeader` reads ten design-system tokens that are declared only inside
 * `MarketingSurface`, so it could not be dropped onto a page that is still the
 * pre-redesign Tailwind library. This carries `.tokens` on its own root, which
 * makes the bar portable without repainting, re-fonting or otherwise touching
 * the page below it – the chrome standardizes ahead of the interior template
 * redesign rather than waiting for it.
 *
 * The wrapper contributes no layout: everything it holds is `position: fixed`,
 * and it declares custom properties only, no background and no containment.
 *
 * It carries the font variables alongside `.tokens` because the two font
 * tokens read them; a page outside a `MarketingSurface` supplies them nowhere
 * else, and the bar would fall back to whatever the page body inherits.
 */
export function SiteHeader({ band, initialTone = 'page' }: SiteHeaderProps): React.ReactElement {
  return (
    <div
      className={clsx(styles.tokens, marketingFontVariables)}
      style={band ? ({ '--bg-page': band } as CSSProperties) : undefined}
    >
      <HomepageHeader initialTone={initialTone} />
    </div>
  );
}
